
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface EmptyStateAlertProps {
  isEmpty: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export const EmptyStateAlert = ({ 
  isEmpty, 
  isLoading, 
  isError,
  refetch 
}: EmptyStateAlertProps) => {
  const { toast } = useToast();

  if (!isEmpty || isLoading || isError) return null;

  const handleCreateProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (!existingProfile) {
        const { error } = await supabase
          .from('profiles')
          .insert([{ 
            id: user.id, 
            email: user.email,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);
          
        if (error) {
          toast({
            title: "Error creating profile",
            description: error.message,
            variant: "destructive"
          });
        } else {
          toast({
            title: "Profile created",
            description: "Your profile has been created. Refreshing data."
          });
          refetch();
        }
      } else {
        toast({
          title: "Profile exists",
          description: "Your profile already exists in the database."
        });
        refetch();
      }
    }
  };

  return (
    <Alert>
      <AlertTitle>No Users Found</AlertTitle>
      <AlertDescription>
        There are no users in the database. This could be due to:
        <ul className="ml-5 mt-2 list-disc">
          <li>The profiles table is empty</li>
          <li>Auth trigger to create profiles is not set up correctly</li>
          <li>Row-level security policies are preventing access</li>
        </ul>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-2"
          onClick={handleCreateProfile}
        >
          Create Profile for Current User
        </Button>
      </AlertDescription>
    </Alert>
  );
};
